import { useMemo } from 'react';

/**
 * Renders a horizontal day-strip showing a member's CW schedule entries with
 * a proposed booking overlaid. Entries that overlap the proposal are flagged
 * red with their identifying summary so admins can dispatch around them.
 *
 * Props:
 *   dayDate — Date object for the day to render (uses local-time hours)
 *   scheduleEntries — array of { id, summary, dateStart, dateEnd, type, status }
 *   proposed — { dateStart, dateEnd } | null. Highlights overlap.
 *   startHour — int. First hour shown (default 7)
 *   endHour — int. Last hour shown (default 19)
 */
export default function MemberDayTimeline({
  dayDate,
  scheduleEntries = [],
  proposed = null,
  startHour = 7,
  endHour = 19,
}) {
  const dayStart = useMemo(() => {
    const d = new Date(dayDate);
    d.setHours(startHour, 0, 0, 0);
    return d.getTime();
  }, [dayDate, startHour]);

  const dayEnd = useMemo(() => {
    const d = new Date(dayDate);
    d.setHours(endHour, 0, 0, 0);
    return d.getTime();
  }, [dayDate, endHour]);

  const totalMs = dayEnd - dayStart;

  const entriesForDay = useMemo(() => {
    const sameDay = (iso) => {
      const d = new Date(iso);
      const ref = new Date(dayDate);
      return (
        d.getFullYear() === ref.getFullYear() &&
        d.getMonth() === ref.getMonth() &&
        d.getDate() === ref.getDate()
      );
    };
    return (scheduleEntries || []).filter(
      (e) => sameDay(e.dateStart) || sameDay(e.dateEnd)
    );
  }, [scheduleEntries, dayDate]);

  const proposedRange = useMemo(() => {
    if (!proposed?.dateStart || !proposed?.dateEnd) return null;
    return {
      start: new Date(proposed.dateStart).getTime(),
      end: new Date(proposed.dateEnd).getTime(),
    };
  }, [proposed]);

  const overlap = (a, b) => a.start < b.end && b.start < a.end;

  const proposedConflicts = useMemo(() => {
    if (!proposedRange) return [];
    return entriesForDay.filter((e) =>
      overlap(proposedRange, {
        start: new Date(e.dateStart).getTime(),
        end: new Date(e.dateEnd).getTime(),
      })
    );
  }, [entriesForDay, proposedRange]);

  const toPercent = (ts) => {
    const clamped = Math.max(dayStart, Math.min(dayEnd, ts));
    return ((clamped - dayStart) / totalMs) * 100;
  };

  const hourMarks = [];
  for (let h = startHour; h <= endHour; h += 1) hourMarks.push(h);

  return (
    <div className="border border-neutral-200 dark:border-neutral-700 rounded-lg p-2 bg-white dark:bg-neutral-800">
      <div className="text-xs font-medium text-neutral-700 dark:text-neutral-300 mb-2">
        {new Date(dayDate).toLocaleDateString(undefined, {
          weekday: 'long',
          month: 'short',
          day: 'numeric',
        })}
      </div>
      <div className="relative h-12 bg-neutral-50 dark:bg-neutral-700/40 rounded">
        {/* Hour gridlines */}
        {hourMarks.map((h) => {
          const ts = new Date(dayDate);
          ts.setHours(h, 0, 0, 0);
          return (
            <div
              key={h}
              className="absolute top-0 bottom-0 border-l border-neutral-200 dark:border-neutral-600"
              style={{ left: `${toPercent(ts.getTime())}%` }}
            >
              <span className="absolute -top-3 -translate-x-1/2 text-[10px] text-neutral-400">
                {h % 12 === 0 ? 12 : h % 12}
                {h < 12 ? 'a' : 'p'}
              </span>
            </div>
          );
        })}
        {/* Existing schedule blocks */}
        {entriesForDay.map((e) => {
          const start = new Date(e.dateStart).getTime();
          const end = new Date(e.dateEnd).getTime();
          const left = toPercent(start);
          const right = toPercent(end);
          const width = Math.max(0.5, right - left);
          const conflict = proposedConflicts.some((c) => c.id === e.id);
          return (
            <div
              key={e.id}
              title={`${e.summary || e.type || 'Entry'}\n${new Date(e.dateStart).toLocaleTimeString()} – ${new Date(e.dateEnd).toLocaleTimeString()}`}
              className={
                'absolute top-1 bottom-1 rounded px-1 text-[10px] truncate ' +
                (conflict
                  ? 'bg-red-200 dark:bg-red-900/60 text-red-900 dark:text-red-100 border border-red-500'
                  : 'bg-blue-200 dark:bg-blue-900/60 text-blue-900 dark:text-blue-100 border border-blue-400')
              }
              style={{ left: `${left}%`, width: `${width}%` }}
            >
              {e.summary || e.type || 'Entry'}
            </div>
          );
        })}
        {/* Proposed block */}
        {proposedRange && (
          <div
            className={
              'absolute top-0 bottom-0 border-2 ' +
              (proposedConflicts.length > 0
                ? 'border-red-600 bg-red-300/40'
                : 'border-green-600 bg-green-300/40')
            }
            style={{
              left: `${toPercent(proposedRange.start)}%`,
              width: `${Math.max(0.5, toPercent(proposedRange.end) - toPercent(proposedRange.start))}%`,
            }}
          />
        )}
      </div>
      {proposedConflicts.length > 0 && (
        <div className="mt-2 text-xs text-red-600 dark:text-red-400">
          Conflicts with:{' '}
          {proposedConflicts
            .map((c) => c.summary || `${c.type || 'Entry'} #${c.objectId || ''}`)
            .join(', ')}
        </div>
      )}
      {entriesForDay.length === 0 && (
        <div className="mt-2 text-xs text-neutral-500 dark:text-neutral-400">
          No existing schedule entries for this day.
        </div>
      )}
    </div>
  );
}
